import { format } from 'date-fns';
import Link from 'next/link';
import React from 'react';
import Balancer from 'react-wrap-balancer';
import { IconArrowRight } from '@tabler/icons-react';

import { BlurImage } from '@/components/blur-image';
import { jakarta } from '@/lib/fonts';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { cn, truncate } from '@/lib/utils';
import { Article } from '@/types/types';

export const BlogCard = ({
  article,
  locale,
}: {
  article: Article;
  locale: string;
}) => {
  return (
    <Link
      className="grid grid-cols-1 lg:grid-cols-12 rounded-3xl group border border-[#e5e2f5] dark:border-neutral-800 hover:border-[#c02029]/40 w-full bg-white dark:bg-neutral-900 overflow-hidden transition-all duration-500 shadow-xl shadow-[#301754]/5 hover:shadow-2xl hover:shadow-[#c02029]/10 mb-12 hover:-translate-y-1"
      href={`/${locale}/blog/${article.slug}`}
    >
      <div className="relative lg:col-span-6 aspect-[16/10] lg:aspect-auto overflow-hidden bg-neutral-100 dark:bg-neutral-950">
        {article.image ? (
          <BlurImage
            src={strapiImage(article.image.url)}
            alt={article.title}
            height="1200"
            width="1200"
            className="h-full object-cover object-center w-full transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 min-h-[16rem]">
            <span className="text-xl font-bold text-[#c02029]">Veosis</span>
          </div>
        )}
      </div>

      <div className="lg:col-span-6 p-6 sm:p-10 md:p-12 flex flex-col justify-between">
        <div>
          <div className="flex gap-2 flex-wrap mb-4">
            {article.categories?.map((category, idx) => (
              <span
                key={`category-${idx}`}
                className="text-xs font-semibold px-3 py-1 rounded-full bg-[#f4f2fc] text-[#50456c] dark:bg-neutral-800 dark:text-neutral-300 capitalize tracking-wide border border-[#e5e2f5] dark:border-neutral-700/60"
              >
                {category.name}
              </span>
            ))}
          </div>

          <h2
            className={cn(
              jakarta.className,
              'text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-[#2e1452] dark:text-white leading-tight group-hover:text-[#c02029] transition-colors'
            )}
          >
            <Balancer>{article.title}</Balancer>
          </h2>

          <p className="text-left text-base sm:text-lg text-[#5e5873] dark:text-neutral-300 leading-relaxed mb-6">
            {truncate(article.description, 260)}
          </p>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-[#e5e2f5] dark:border-neutral-800">
          <time
            dateTime={article.publishedAt}
            className="text-xs sm:text-sm font-medium text-[#50456c] dark:text-neutral-400"
          >
            {format(new Date(article.publishedAt), 'MMMM dd, yyyy')}
          </time>

          <div className="inline-flex items-center gap-3">
            <span className="text-sm font-semibold text-[#2e1452] dark:text-neutral-200 group-hover:text-[#c02029] transition-colors">
              {locale === 'fr' ? 'Lire l’article' : 'Read article'}
            </span>
            <span className="w-9 h-9 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0">
              <IconArrowRight className="w-4 h-4 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const BlogCardVertical = ({
  article,
  locale,
}: {
  article: Article;
  locale: string;
}) => {
  return (
    <Link
      className="flex flex-col rounded-3xl group border border-[#e5e2f5] dark:border-neutral-800 hover:border-[#c02029]/40 w-full bg-white dark:bg-neutral-900 overflow-hidden transition-all duration-500 shadow-xl shadow-[#301754]/5 hover:shadow-2xl hover:shadow-[#c02029]/10 hover:-translate-y-1.5"
      href={`/${locale}/blog/${article.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100 dark:bg-neutral-950 border-b border-[#e5e2f5] dark:border-neutral-800">
        {article.image ? (
          <BlurImage
            src={strapiImage(article.image.url || '')}
            alt={article.title}
            height="800"
            width="800"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-900">
            <span className="text-lg font-bold text-[#c02029]">Veosis</span>
          </div>
        )}
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1 justify-between">
        <div>
          <div className="flex gap-2 flex-wrap mb-4">
            {article.categories?.map((category, idx) => (
              <span
                key={`category-${idx}`}
                className="text-xs font-semibold px-3 py-1 rounded-full bg-[#f4f2fc] text-[#50456c] dark:bg-neutral-800 dark:text-neutral-300 capitalize tracking-wide border border-[#e5e2f5] dark:border-neutral-700/60"
              >
                {category.name}
              </span>
            ))}
          </div>

          <h2
            className={cn(
              jakarta.className,
              'text-xl sm:text-2xl font-bold mb-3 text-[#2e1452] dark:text-white leading-snug group-hover:text-[#c02029] transition-colors'
            )}
          >
            <Balancer>{article.title}</Balancer>
          </h2>

          <p className="text-left text-sm sm:text-base text-[#5e5873] dark:text-neutral-300 leading-relaxed mb-6">
            {truncate(article.description, 160)}
          </p>
        </div>

        <div className="flex items-center justify-between pt-5 border-t border-[#e5e2f5] dark:border-neutral-800">
          <time
            dateTime={article.publishedAt}
            className="text-xs font-medium text-[#50456c] dark:text-neutral-400"
          >
            {format(new Date(article.publishedAt), 'MMMM dd, yyyy')}
          </time>

          <span className="w-8 h-8 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0">
            <IconArrowRight className="w-4 h-4 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
